import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

const WEEKDAYS: readonly DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export interface WorkingHoursValue {
  mon?: { open: string; close: string };
  tue?: { open: string; close: string };
  wed?: { open: string; close: string };
  thu?: { open: string; close: string };
  fri?: { open: string; close: string };
  sat?: { open: string; close: string };
  sun?: { open: string; close: string };
  closed_days: DayKey[];
}

interface WorkingHoursGridProps {
  value: WorkingHoursValue | undefined;
  onChange: (value: WorkingHoursValue) => void;
}

const EMPTY: WorkingHoursValue = { closed_days: [] };

export function WorkingHoursGrid({ value, onChange }: WorkingHoursGridProps) {
  const { t } = useTranslation();
  const current = value ?? EMPTY;
  // `working_hours` is persisted as opaque JSONB (backend `fields.Raw`), so a
  // committed/legacy profile can hydrate this grid with an object that lacks
  // `closed_days`. Normalise defensively — reading `.includes` off `undefined`
  // would throw and (no ErrorBoundary) break the whole onboarding section.
  const closedDays = current.closed_days ?? [];

  function toggleClosed(day: DayKey) {
    const isClosed = closedDays.includes(day);
    const closed_days = isClosed ? closedDays.filter((d) => d !== day) : [...closedDays, day];
    const next: WorkingHoursValue = { ...current, closed_days };
    if (!isClosed) delete next[day];
    onChange(next);
  }

  function setTime(day: DayKey, slot: 'open' | 'close', val: string) {
    const day_value = current[day] ?? { open: '', close: '' };
    onChange({ ...current, closed_days: closedDays, [day]: { ...day_value, [slot]: val } });
  }

  return (
    <div data-testid="working-hours-grid" className="flex flex-col gap-2">
      {WEEKDAYS.map((day) => {
        const closed = closedDays.includes(day);
        const slots = current[day];
        return (
          <div key={day} className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="w-28 text-sm font-medium">{t(`onboarding.kb.weekday_${day}`)}</div>
            <label className="flex items-center gap-2 text-sm min-h-11">
              <input
                type="checkbox"
                checked={closed}
                onChange={() => toggleClosed(day)}
                aria-label={t('onboarding.kb.closed_label', {
                  day: t(`onboarding.kb.weekday_${day}`),
                })}
              />
              {t('onboarding.kb.closed')}
            </label>
            {!closed && (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  className="h-11 w-24"
                  placeholder="--:--"
                  value={slots?.open ?? ''}
                  onChange={(e) => setTime(day, 'open', e.target.value)}
                  aria-label={t('onboarding.kb.open_label', {
                    day: t(`onboarding.kb.weekday_${day}`),
                  })}
                />
                <span className="text-muted-foreground">—</span>
                <Input
                  className="h-11 w-24"
                  placeholder="--:--"
                  value={slots?.close ?? ''}
                  onChange={(e) => setTime(day, 'close', e.target.value)}
                  aria-label={t('onboarding.kb.close_label', {
                    day: t(`onboarding.kb.weekday_${day}`),
                  })}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
